import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Footer } from "@/components/layout/Footer";
import { ParallaxImage } from "@/components/motion/ParallaxImage";
import { BackgroundVideo } from "@/components/motion/BackgroundVideo";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import { StackPanel } from "@/components/motion/StackPanel";
import { Header } from "@/components/layout/Header";
import { Picture } from "@/components/media/Picture";

import { UPCOMING_CRUSADES } from "@/data/crusades";
import { heroFor } from "@/data/heroes";

import { site } from "@/data/site";
import { supabase } from "@/integrations/supabase/client";

const TITLE = "Light of Life International — Evangelistic & Compassion Ministry";
const DESCRIPTION =
  "Light of Life International is an evangelistic and compassionate ministry reaching Latin America and Africa with gospel crusades, compassion outreach and mission teams.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/" },
    ],
    links: [
      { rel: "canonical", href: "https://loli.ardent.dev/" },
      {
        rel: "preload",
        as: "image",
        href: "/media/loli-slider-1-1600.avif",
        imageSrcSet:
          "/media/loli-slider-1-640.avif 640w, /media/loli-slider-1-1024.avif 1024w, /media/loli-slider-1-1600.avif 1600w",
        imageSizes: "100vw",
        type: "image/avif",
      },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: site.name,
          alternateName: site.shortName,
          url: "/",
          email: site.email,
          telephone: site.phone,
          address: { "@type": "PostalAddress", streetAddress: site.address },
          sameAs: site.socials.map((s) => s.href),
        }),
      },
    ],
  }),
  component: HomePage,
});

/**
 * Self-hosted "Who We Are" background loop (no third-party player).
 * Mounted lazily by BackgroundVideo so first paint stays fast.
 */
const HOME_VIDEO_SRC = "/media/who-we-are-loop.mp4";

import fireworksHero from "@/assets/fireworks-hero.png.asset.json";

const heroSlides = [
  "/media/loli-slider-1.jpg",
  "/media/loli-slider-2.jpg",
  fireworksHero.url,
  "/media/loli-slider-4.jpg",
  "/media/loli-crusades-hero.jpg",
  "/media/fire-carriers-hero.jpg",
  "/media/join-us-hero-group.jpg",
  "/media/vm06.webp",
  "/media/vm05.webp",
];

const coreValues = [
  { title: "Passion For Souls", image: "/media/s4yx6gdjlioe4cqlpbke.webp" },
  { title: "Compassion For The Poor", image: "/media/p7ye5zbudfiljzhzfnzd.webp" },
  { title: "Transformation By Unity & Prayer", image: "/media/oanvxttyoavlui0wcko8.webp" },
  { title: "Destiny", image: "/media/w8v1fcqhkqh8mrnjujqv.webp" },
];

const pastCrusades = [
  { city: "Nicoadala, Mozambique", year: "2026", image: "/media/vm07.webp" },
  { city: "Willemstad, Curaçao", year: "2026", image: "/media/vm01.webp" },
  { city: "La Entrada, Honduras", year: "2026", image: "/media/vm02.webp" },
  { city: "Paramaribo, Suriname", year: "2026", image: "/media/vm03.webp" },
  { city: "Saint-Laurent, French Guiana", year: "2026", image: "/media/vm04.webp" },
  { city: "Georgetown, Guyana", year: "2026", image: "/media/vm05.webp" },
];

const marqueeImages = [
  "/media/vm01.webp",
  "/media/vm02.webp",
  "/media/vm03.webp",
  "/media/vm04.webp",
  "/media/vm05.webp",
  "/media/vm06.webp",
  "/media/vm07.webp",
  "/media/vm08.webp",
];

const buttonOutline =
  "inline-block rounded border-2 border-white px-8 py-3 font-heading text-sm uppercase tracking-widest text-white transition-colors hover:border-brand hover:bg-brand hover:text-brand-foreground";
const buttonSolid =
  "inline-block rounded bg-brand px-8 py-3 font-heading text-sm uppercase tracking-widest text-brand-foreground transition-opacity hover:opacity-90";

function PhotoMarquee({
  reverse = false,
  variant = "square",
}: {
  reverse?: boolean;
  variant?: "wide" | "square";
}) {
  const strip = [...marqueeImages, ...marqueeImages];
  const itemClasses =
    variant === "wide"
      ? "mx-2 h-48 w-80 flex-none overflow-hidden rounded-2xl border-4 border-white bg-white shadow-lg sm:mx-3 sm:h-60 sm:w-[28rem]"
      : "mx-2 h-48 w-48 flex-none overflow-hidden rounded-2xl border-4 border-white bg-white shadow-lg sm:mx-3 sm:h-56 sm:w-56";
  return (
    <div className="marquee-pause overflow-hidden py-2" aria-hidden>
      <div className={reverse ? "marquee-track-reverse" : "marquee-track"}>
        {strip.map((src, i) => (
          <div key={`${src}-${i}`} className={itemClasses}>
            <Picture
              src={src}
              alt=""
              sizes={variant === "wide" ? "(max-width: 640px) 20rem, 28rem" : "14rem"}
              widths={[640, 1024]}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function HomePage() {
  const [slide, setSlide] = useState(0);
  // High-water mark: slides mount as the rotation reaches them and never unmount.
  const [loadedUpTo, setLoadedUpTo] = useState(0);

  useEffect(() => {
    // Respect visitors who ask for reduced motion: keep the first photo still
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setLoadedUpTo((current) => Math.max(current, slide + 1));
  }, [slide]);

  const { data: posts } = useQuery({
    queryKey: ["home-latest-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select(
          "id, slug, title, excerpt, published_at, media:featured_media_id(public_url, alt_text)",
        )
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="min-h-dvh bg-background">
      <Header />

      <main id="main">
        {/* Hero — upcoming crusades, full screen slider */}
        <StackPanel z={1} label="Upcoming crusades">
          {heroSlides.map((src, i) =>
            // Only the first frame is fetched with the document; the rest mount
            // once the rotation has started so first paint stays lightweight.
            i <= loadedUpTo ? (
              <Picture
                key={src}
                src={src}
                alt=""
                sizes="100vw"
                width={1600}
                height={800}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "low"}
                decoding={i === 0 ? "sync" : "async"}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  i === slide ? "opacity-100" : "opacity-0"
                }`}
              />
            ) : null,
          )}
          <div className="hero-scrim absolute inset-0" aria-hidden />
          <RevealGroup className="container-site relative flex flex-col items-center py-24 text-center sm:py-32">
            <RevealItem>
              <h1 className="text-5xl text-white sm:text-8xl">
                <span className="block font-normal">
                  {UPCOMING_CRUSADES[slide % UPCOMING_CRUSADES.length]?.city}
                </span>
                <span className="block font-bold">
                  {UPCOMING_CRUSADES[slide % UPCOMING_CRUSADES.length]?.country}
                </span>
              </h1>
            </RevealItem>
            <RevealItem>
              <p className="eyebrow mt-5 text-xl text-white sm:text-2xl">
                {UPCOMING_CRUSADES[slide % UPCOMING_CRUSADES.length]?.dates}
              </p>
            </RevealItem>
            <RevealItem>
              <p className="mt-5 max-w-2xl text-lg text-white sm:text-xl">
                Welcome to Light of Life International evangelistic ministry.
              </p>
            </RevealItem>
            <RevealItem>
              <Link to="/join-us" className={`${buttonOutline} mt-10`}>
                Join the Mission
              </Link>
            </RevealItem>
          </RevealGroup>
        </StackPanel>

        {/* Core values — full-bleed photo tiles */}
        <section className="relative z-[2] -mt-10 overflow-hidden rounded-t-[2.5rem] bg-background py-20 shadow-2xl">
          <Reveal className="container-site">
            <h2 className="text-center text-4xl text-navy sm:text-5xl">
              Our Core <span className="text-brand">Values</span>
            </h2>
          </Reveal>
          <RevealGroup
            as="ul"
            className="container-site mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {coreValues.map((value) => (
              <RevealItem
                as="li"
                key={value.title}
                className="group relative overflow-hidden rounded-2xl shadow-lg"
              >
                <Picture
                  src={value.image}
                  alt=""
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  widths={[640, 1024]}
                  loading="lazy"
                  decoding="async"
                  className="h-[300px] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />

                <div className="hero-scrim absolute inset-0 flex items-center justify-center p-5">
                  <h3 className="text-center text-2xl uppercase leading-tight text-white">
                    {value.title}
                  </h3>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>

          <Reveal className="mt-12 text-center">
            <Link to="/about" className={buttonSolid}>
              About Our Ministry
            </Link>
          </Reveal>
        </section>

        {/* Who we are — self-hosted background loop, still photo until it plays */}
        <StackPanel z={3} label="Who we are">
          <BackgroundVideo
            src={HOME_VIDEO_SRC}
            poster="/media/home-quote-scaled.jpg"
            title="Light of Life International ministry background loop"
          />
          <div className="video-scrim absolute inset-0" aria-hidden />
          <RevealGroup className="container-site relative max-w-3xl py-24 text-center">
            <RevealItem>
              <h2 className="text-4xl text-white sm:text-6xl">
                Who <span className="text-brand">We Are</span>
              </h2>
            </RevealItem>
            <RevealItem>
              <p className="mt-6 text-lg text-white sm:text-xl">
                Light of Life International is an evangelistic and compassionate ministry reaching
                Latin America and Africa. For over 21 years, the Holy Spirit has led us into
                hospitals, orphanages, public schools, as well as soccer stadiums and city plazas to
                proclaim the Good News that Jesus saves.
              </p>
            </RevealItem>
          </RevealGroup>
        </StackPanel>

        {/* Upcoming crusades — full screen */}
        <StackPanel z={4} label="Our upcoming crusades">
          <ParallaxImage src="/media/crusades-loop-background-updated.jpg" strength={6} />
          <div className="absolute inset-0 bg-black/75" aria-hidden />
          <RevealGroup className="container-site relative flex flex-col items-center justify-center py-24 text-center sm:py-32">
            <RevealItem>
              <p className="eyebrow text-sm text-white/90">Our Upcoming Crusades</p>
            </RevealItem>
            <RevealItem className="w-full">
              <ul className="mt-8 grid w-full gap-8 sm:grid-cols-2">
                {UPCOMING_CRUSADES.map((crusade) => (
                  <li key={crusade.city}>
                    <Link
                      to="/join-us"
                      hash="jointeam"
                      className="group relative block overflow-hidden rounded-2xl border border-white/40 p-8 text-center transition-all duration-300 hover:border-white/80 hover:backdrop-blur-md hover:shadow-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-4 focus-visible:ring-offset-black"
                      aria-label={`Join the ${crusade.city}, ${crusade.country} crusade`}
                    >
                      <div className="relative z-10">
                        <h2 className="text-4xl text-white drop-shadow-lg transition-transform duration-300 group-hover:scale-105 sm:text-5xl">
                          {crusade.city}
                          <span className="block font-bold">{crusade.country}</span>
                        </h2>
                        <p className="eyebrow mt-4 text-lg text-white drop-shadow-md sm:text-xl">
                          {crusade.dates}
                        </p>
                        <span
                          className={`${buttonOutline} relative z-10 mt-6 transition-colors duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-brand-foreground`}
                        >
                          Sign Up
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </RevealItem>
          </RevealGroup>
        </StackPanel>

        {/* Past crusades */}
        <section className="relative z-[5] -mt-10 overflow-hidden rounded-t-[2.5rem] bg-background section-y shadow-2xl">
          <div className="container-site">
            <Reveal>
              <h2 className="text-center text-4xl text-navy sm:text-5xl">Past Crusades</h2>
            </Reveal>
            <RevealGroup as="ul" className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {pastCrusades.map((item) => (
                <RevealItem as="li" key={item.city} className="relative overflow-hidden rounded-lg">
                  <Picture
                    src={item.image}
                    alt={item.city}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    widths={[640, 1024]}
                    loading="lazy"
                    decoding="async"
                    className="h-72 w-full object-cover"
                  />

                  <div className="hero-scrim absolute inset-0 flex flex-col items-center justify-center p-4">
                    <h3 className="text-center text-2xl text-white">{item.city}</h3>
                    <p className="eyebrow mt-2 text-sm text-white/90">{item.year}</p>
                  </div>
                </RevealItem>
              ))}
            </RevealGroup>
            <Reveal className="mt-10 text-center">
              <Link to="/crusades" className={buttonSolid}>
                See More
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Daystar */}
        <section className="relative z-[6] bg-muted py-16">
          <Reveal className="container-site max-w-4xl">
            <Picture
              src="/media/Daystar1-1-1024x449.jpg"
              alt="Light of Life International on Daystar Television"
              sizes="(max-width: 1024px) 100vw, 56rem"
              widths={[640, 1024]}
              loading="lazy"
              decoding="async"
              className="w-full rounded-lg"
            />
          </Reveal>
        </section>

        {/* Destiny quote — full screen band */}
        <StackPanel z={7} label="Destiny">
          <ParallaxImage src="/media/LOLI_Events_1-1-scaled.jpeg" />
          <div className="hero-scrim absolute inset-0" aria-hidden />
          <Reveal className="container-site relative max-w-3xl py-24 text-center">
            <h2 className="text-4xl text-white sm:text-6xl">
              God has a beautiful destiny for everyone, and that includes you.
            </h2>
          </Reveal>
        </StackPanel>

        {/* Fire Carriers — full-bleed fire background */}
        <section className="relative z-[8] overflow-hidden py-24 sm:py-28">
          <Picture
            src={heroFor("/fire-carriers").image}
            alt=""
            aria-hidden
            sizes="100vw"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div
            className="absolute inset-0 bg-gradient-to-r from-[#c2410c]/90 via-[#b3350f]/85 to-[#4c1d5a]/90"
            aria-hidden
          />
          <div className="container-site relative grid items-center gap-12 md:grid-cols-[1.35fr_1fr]">
            <Reveal direction="left">
              <h2 className="text-3xl text-white sm:text-5xl">
                <span className="text-brand">You Shall Receive Power!</span> What Happens When
                Average People Dare To Dream with an Extraordinary God?
              </h2>
              <p className="mt-6 text-white/90">
                Fire Carriers is out now! Pick up your copy today!
              </p>
              <Link to="/fire-carriers" className={`${buttonSolid} mt-8`}>
                Order Now
              </Link>
            </Reveal>
            <Reveal className="flex justify-center" delay={0.15}>
              <img
                src="/media/FC_transparent_3D_large-218x300-1.webp"
                alt="Fire Carriers book"
                loading="lazy"
                decoding="async"
                className="w-56 drop-shadow-2xl sm:w-64"
              />
            </Reveal>
          </div>
        </section>

        {/* Fire Starters & Rain Crusaders */}
        <section className="relative z-[9] -mt-10 overflow-hidden rounded-t-[2.5rem] bg-white section-y">
          <div className="container-site grid items-center gap-12 md:grid-cols-2">
            <Reveal className="flex justify-center md:order-2" delay={0.15}>
              <img
                src="/media/FSRC_3D_transparent-250x300-1.webp"
                alt="Fire Starters & Rain Crusaders book"
                loading="lazy"
                decoding="async"
                className="w-56 sm:w-64"
              />
            </Reveal>
            <Reveal direction="right">
              <h2 className="text-3xl text-navy sm:text-4xl">
                Read more about the crusades, miracles, and testimonies of{" "}
                <span className="text-brand">Light of Life International.</span>
              </h2>
              <p className="mt-5 text-foreground">Pick up your copy today!</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=KEPANMHB96ZXS"
                  target="_blank"
                  rel="noreferrer"
                  className={buttonSolid}
                >
                  Order Now
                </a>
                <Link
                  to="/fire-starters-and-rain-crusaders"
                  className="font-heading text-sm uppercase tracking-widest text-navy underline decoration-brand decoration-2 underline-offset-4"
                >
                  About Fire Starters &amp; Rain Crusaders
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Serving with love — full screen */}
        <StackPanel z={10} label="Serving with love">
          <ParallaxImage src="/media/Home_JoinUs-1.webp" />
          <div className="hero-scrim absolute inset-0" aria-hidden />
          <RevealGroup className="container-site relative max-w-3xl py-24 text-center">
            <RevealItem>
              <p className="text-3xl text-brand sm:text-4xl font-heading uppercase">
                Serving with love
              </p>
            </RevealItem>
            <RevealItem>
              <h2 className="mt-5 text-4xl text-white sm:text-5xl">
                Take a look at what the Lord Jesus has done across Latin America and Africa!
              </h2>
            </RevealItem>
            <RevealItem>
              <Link to="/contact" className={`${buttonOutline} mt-10`}>
                Contact Us
              </Link>
            </RevealItem>
          </RevealGroup>
        </StackPanel>

        {/* Latest blog posts */}
        <section className="relative z-[11] -mt-10 overflow-hidden rounded-t-[2.5rem] bg-background section-y shadow-2xl">
          <div className="container-site">
            <Reveal>
              <h2 className="text-center text-4xl text-navy sm:text-5xl">
                Lastest <span className="text-brand">Blog Posts</span>
              </h2>
            </Reveal>
            {posts && posts.length > 0 ? (
              <RevealGroup as="ul" className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
                {posts.map((post) => (
                  <RevealItem as="li" key={post.id} className="flex flex-col">
                    {post.media?.public_url ? (
                      <Link to="/blog/$slug" params={{ slug: post.slug }}>
                        <img
                          src={post.media.public_url}
                          alt={post.media.alt_text ?? post.title}
                          loading="lazy"
                          decoding="async"
                          className="h-56 w-full rounded-lg object-cover"
                        />
                      </Link>
                    ) : null}
                    <h3 className="mt-5 text-2xl text-navy">
                      <Link to="/blog/$slug" params={{ slug: post.slug }}>
                        {post.title}
                      </Link>
                    </h3>
                    {post.excerpt ? <p className="mt-3 text-foreground">{post.excerpt}</p> : null}
                  </RevealItem>
                ))}
              </RevealGroup>
            ) : (
              <p className="mt-8 text-center text-muted-foreground">
                New ministry updates and testimonies are coming soon.
              </p>
            )}
            <Reveal className="mt-12 text-center">
              <Link to="/blog" className={buttonSolid}>
                View More Posts
              </Link>
            </Reveal>
          </div>
        </section>

        {/* Scrolling crusade photo strips */}
        <div className="relative z-[12] bg-background pb-6">
          <Reveal>
            <PhotoMarquee variant="wide" />
          </Reveal>
          <Reveal>
            <PhotoMarquee variant="square" reverse />
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
